import * as React from "react";
import Modal from "../../Modal/Modal";
import Input from "../../Input-new/Input";
import { downloadRepAgreementWord } from "../../../requests/repAgreement";
import {
    escapeHtml,
    docWrap,
    propertyNameOf,
    moneyWords,
    moneyNumeral,
    smallWords,
    lowerWords,
    cleanNumeral,
    shortDate,
    resolveSeller,
    resolveListingAgents,
    brokerOfRecordFor,
    buildAgentSignatureSection,
} from "./RepAgreementForm";

/**
 * Rep Amendment button + generator (Rep Agreement tab).
 *
 * A listing already under a Representation Agreement is changed by a short
 * document rather than a new agreement, and which document depends on what is
 * being changed:
 *
 *   - a new price, or a longer term  -> Amendment to Representation Agreement
 *   - a property being added         -> Addendum to Representation Agreement
 *
 * Everything else is shared with the Rep Agreement itself and imported from it:
 * the stylesheet, the page setup, the signature blocks, the money and date
 * helpers, and the Word conversion. Generate prints; Word downloads the .docx.
 * Neither is stored - the Rep Agreement is download-only and these match it.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

// Dates reach the form as MM/DD/YYYY (see convertDataToForm); the agreements
// cite them long-form, e.g. "December 1, 2025".
const longDate = (value) => {
    const parts = String(value == null ? '' : value).trim().split('/');
    if (parts.length !== 3) return '';
    const [month, day, year] = parts.map(Number);
    if (!MONTHS[month - 1] || !day || !year) return '';
    return `${MONTHS[month - 1]} ${day}, ${year}`;
};

const BLANK_RULE = '____________________';

const filled = (value) => String(value == null ? '' : value).trim() !== '';

// Which document a submission produces: adding a property is an addendum,
// anything else is an amendment. Each clause is written only if the field
// behind it was filled in, so a field left empty simply leaves its clause out.
const AMENDMENT = 'AMENDMENT';
const ADDENDUM = 'ADDENDUM';

const documentTypeFor = (form = {}) => (filled(form.newProperty) ? ADDENDUM : AMENDMENT);

const emptyForm = () => ({ sellerEntity: '', newPrice: '', extendedTerm: '6', newProperty: '' });

// "two and a half percent (2.5%)" reads lower case in these clauses, the same
// way it does in the agreement itself.
const spelled = (value) => {
    const numeral = cleanNumeral(value);
    return { words: lowerWords(smallWords(numeral) || numeral), numeral };
};

const priceText = (value) =>
    (filled(value) ? `${moneyWords(value)} (${moneyNumeral(value)})` : BLANK_RULE);

/** SELLER: <entity> / BY / DATE / Name, as both templates set it out. */
const sellerSignature = (sellerEntity) => `
    <div class="sig-block">
        <div class="sig-heading">SELLER: ${escapeHtml(sellerEntity || BLANK_RULE)}</div>
        <div class="sig-line">BY: __________________________________&nbsp;&nbsp;&nbsp;&nbsp;DATE: ______________</div>
        <div class="sig-name" style="margin-left:0;">Name: _______________________________</div>
    </div>
`;

// The closing disclaimer. In the source documents it sits in a borderless
// single-cell table, which is a layout box rather than anything visible, so it
// prints as the same bold paragraph the state agreements use.
const closingDisclaimer = (documentWord) => `
    <p style="margin-top:20px;"><strong>NO REPRESENTATION IS MADE BY AGENT AS TO THE LEGAL EFFECT OF THIS ${escapeHtml(documentWord)}.&nbsp; A REAL ESTATE BROKER IS QUALIFIED TO GIVE ADVICE ON REAL ESTATE MATTERS.&nbsp; IF YOU DESIRE LEGAL, FINANCIAL OR TAX ADVICE, CONSULT YOUR ATTORNEY, ACCOUNTANT OR TAX ADVISOR.</strong></p>
`;

// --- Amendment to Representation Agreement (price and/or term) ---

const buildAmendmentHtml = (ctx) => {
    const { sellerEntity, agreementDate, newPrice, extendedTerm, listingAgents, brokerOfRecord, documentTitle } = ctx;

    const term = filled(extendedTerm) ? spelled(extendedTerm) : null;

    const body = `
    <h1>AMENDMENT TO REPRESENTATION AGREEMENT</h1>

    <p>This document is an amendment (&quot;Amendment&quot;) to the Representation Agreement (Exclusive Right to Sell or Exchange) (&ldquo;Agreement&rdquo;) between Summit RE (&ldquo;Agent&rdquo;) and ${escapeHtml(sellerEntity || BLANK_RULE)} (&quot;Seller&quot;) dated ${escapeHtml(agreementDate || BLANK_RULE)}.</p>

    <p>Seller and Agent agree to modify the Agreement as follows:</p>
    ${filled(newPrice) ? `
    <p>Section 1 of the Agreement (PURCHASE PRICE/TERMS) is replaced with the following:</p>

    <p><strong>PURCHASE PRICE/TERMS:</strong> Agent is authorized to offer the Property for sale at a gross purchase price of ${escapeHtml(priceText(newPrice))}, in cash, or upon such other terms and conditions as Seller may approve in writing, or upon such other terms and conditions as seller may approve in writing.</p>
    ` : ''}${term ? `
    <p>The &ldquo;Term&rdquo; of the Agreement (as defined in Section 2) is hereby extended for a period of ${escapeHtml(term.words)} (${escapeHtml(term.numeral)}) months from the date the Seller executes this Amendment.</p>
    ` : ''}
    <div class="sig-group">
        ${sellerSignature(sellerEntity)}
        ${buildAgentSignatureSection('AGENT', listingAgents, brokerOfRecord, { extraGapBeforeHeading: true })}
    </div>
    ${closingDisclaimer('AMENDMENT')}
    `;

    return docWrap(documentTitle, body);
};

// --- Addendum to Representation Agreement (adding a property) ---

const buildAddendumHtml = (ctx) => {
    const { sellerEntity, agreementDate, newPrice, extendedTerm, newProperty, listingAgents, brokerOfRecord, documentTitle } = ctx;

    const term = filled(extendedTerm) ? spelled(extendedTerm) : null;

    const body = `
    <h1>ADDENDUM TO REPRESENTATION AGREEMENT</h1>

    <p>This document is an addendum (&quot;Addendum&quot;) to the Representation Agreement (Exclusive Right to Sell or Exchange) (&ldquo;Agreement&rdquo;) between Summit RE (&ldquo;Agent&rdquo;) and ${escapeHtml(sellerEntity || BLANK_RULE)} (&quot;Seller&quot;) dated ${escapeHtml(agreementDate || BLANK_RULE)}.</p>

    <p>Seller and Agent agree to modify the Agreement as follows:</p>

    <p>The following Property is added to the Agreement with the following purchase price${term ? ', and the Term of the Agreement (Section 2) is hereby extended as follows' : ''}:</p>

    <p>Property: ${escapeHtml(newProperty)} (&ldquo;New Property&rdquo;)</p>

    <p>Purchase Price:&nbsp; Agent is authorized to offer the New Property for sale at a gross purchase price of ${escapeHtml(priceText(newPrice))}, in cash, or upon such other terms and conditions as Seller may approve in writing.</p>
    ${term ? `
    <p>The &ldquo;Term&rdquo; of the Agreement (as defined in Section 2) is hereby extended for a period of ${escapeHtml(term.words)} (${escapeHtml(term.numeral)}) months from the date the Seller executes this Addendum.</p>
    ` : ''}
    <div class="sig-group">
        ${sellerSignature(sellerEntity)}
        ${buildAgentSignatureSection('AGENT', listingAgents, brokerOfRecord, { extraGapBeforeHeading: true })}
    </div>
    ${closingDisclaimer('ADDENDUM')}
    `;

    return docWrap(documentTitle, body);
};

const BUILDERS = { [AMENDMENT]: buildAmendmentHtml, [ADDENDUM]: buildAddendumHtml };

// Named the way the Rep Agreement names its own download, the generation date
// included: "Amendment to Rep Agreement - AEG Vision - Burleson, TX - 8.27.26".
const documentTitleFor = (type, data = {}) => [
    type === ADDENDUM ? 'Addendum to Rep Agreement' : 'Amendment to Rep Agreement',
    data.NAME,
    [data.CITY, data.STATE].filter(Boolean).join(', '),
    shortDate(),
].filter(Boolean).join(' - ');

const RepAmendmentForm = ({ data = {}, viewMode = false, deleted = false }) => {
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(() => emptyForm());

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const reset = () => {
        setForm(emptyForm());
        setOpen(false);
    };

    // Extended Term starts at six months, the usual extension; clearing it
    // just leaves the Term clause out. Seller Entity fills in from the
    // property's current Investor(s) once the lookup returns, without
    // clobbering anything already typed.
    const openForm = () => {
        setForm(emptyForm());
        setOpen(true);
        resolveSeller(data).then((seller) => {
            if (!seller || !seller.name) return;
            setForm((prev) => ({ ...prev, sellerEntity: prev.sellerEntity || seller.name }));
        });
    };

    const type = documentTypeFor(form);

    const buildDocument = async () => {
        const listingAgents = await resolveListingAgents(data);
        const documentTitle = documentTitleFor(type, data);

        const ctx = {
            listingAgents,
            brokerOfRecord: brokerOfRecordFor(data),
            propertyName: propertyNameOf(data),
            sellerEntity: form.sellerEntity,
            agreementDate: longDate(data.LISTED_DATE),
            newPrice: form.newPrice,
            extendedTerm: form.extendedTerm,
            newProperty: form.newProperty,
            documentTitle,
        };

        return { html: BUILDERS[type](ctx), title: documentTitle };
    };

    const generate = async () => {
        const { html, title } = await buildDocument();

        // Print via a hidden iframe so the dialog opens in place, the same way
        // the Rep Agreement prints.
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';

        // Chrome names the saved PDF after the browser tab rather than the frame
        // it is printing, so the tab wears the document's name - the same name
        // the Word download uses - until the dialog closes.
        const previousTitle = document.title;
        const cleanup = () => {
            document.title = previousTitle;
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };

        iframe.onload = () => {
            const cw = iframe.contentWindow;
            cw.focus();
            cw.onafterprint = cleanup;
            cw.print();
            setTimeout(cleanup, 60000);
        };

        document.title = title;
        iframe.srcdoc = html;
        document.body.appendChild(iframe);

        reset();
    };

    const downloadWord = async () => {
        const { html, title } = await buildDocument();
        await downloadRepAgreementWord(html, title);
        reset();
    };

    return (
        <React.Fragment>
            <div className="Rep-agreement_column_item" style={{ minHeight: 0, margin: 0, marginBottom: '16px' }}>
                <button
                    type="button"
                    className="button-new"
                    style={{ border: '1px solid rgb(144, 185, 237)', color: 'rgb(144, 185, 237)', background: 'transparent', width: 'auto', padding: '0px 16px', margin: 0 }}
                    disabled={deleted}
                    onClick={(e) => { e.preventDefault(); openForm(); }}
                >
                    AMENDMENT TO REP
                </button>
            </div>

            <Modal className="loi-main" show={open} onClose={reset}>
                <div className="loi-modal">
                    <span className="green-title"> Rep Amendment </span>
                    <div className="close-button" onClick={reset} />
                </div>
                <div
                    className="loi-modal-content"
                    style={{ display: 'grid', gap: '4px 40px', gridTemplateColumns: '1fr 1fr' }}
                >
                    <Input
                        name="sellerEntity"
                        label="Seller Entity"
                        value={form.sellerEntity}
                        handleParentChange={update}
                    />
                    <Input
                        name="newPrice"
                        label="New Price"
                        valueType="integer"
                        isDollar
                        value={form.newPrice}
                        handleParentChange={update}
                    />
                    <Input
                        name="extendedTerm"
                        label="Extended Term (months)"
                        valueType="number"
                        value={form.extendedTerm}
                        handleParentChange={update}
                    />
                    <Input
                        name="newProperty"
                        label="New Property"
                        value={form.newProperty}
                        handleParentChange={update}
                    />
                </div>
                <div className="modal-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '16px' }}>
                    <button type="button" className="button-new positive" onClick={generate}>Generate</button>
                    <button type="button" className="button-new negative" onClick={downloadWord}>Word</button>
                    <button type="button" className="button-new negative" onClick={reset}>Cancel</button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default RepAmendmentForm;
